"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { cn } from "@/lib/utils";
import { MapPin, Users, Phone, Calendar, Info } from "lucide-react";

type Neighborhood = {
    id: number;
    name: string;
    chief_name: string;
    chief_phone: string;
    population: string;
    creation_date: string;
    description: string;
};

export function DistrictMap() {
    const supabase = createClient();
    const [districts, setDistricts] = useState<Neighborhood[]>([]);
    const [selectedDistrict, setSelectedDistrict] = useState<Neighborhood | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            const { data } = await supabase.from('neighborhoods').select('*').order('name', { ascending: true });
            if (data && data.length > 0) {
                setDistricts(data);
                setSelectedDistrict(data[0]); // Select first by default
            }
            setLoading(false);
        }
        fetchData();
    }, []);

    if (loading) return <div className="text-white text-center p-10">Chargement de la carte des quartiers...</div>;
    if (districts.length === 0) return <div className="text-gray-500 text-center p-10">Aucun quartier enregistré.</div>;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* Interactive List (Acting as Map Selector) */}
            <div className="lg:col-span-2 bg-secondary/50 rounded-xl p-6 border border-white/5 relative overflow-hidden min-h-[500px] flex flex-col">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                    <MapPin className="mr-2 text-primary" />
                    quartiers de Sébikotane
                </h3>

                {/* Scrollable Grid of Neighborhoods */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 overflow-y-auto custom-scrollbar pr-2 max-h-[500px]">
                    {districts.map((district) => (
                        <button
                            key={district.id}
                            onClick={() => setSelectedDistrict(district)}
                            className={cn(
                                "p-3 rounded-lg text-left transition-all border text-sm flex flex-col items-center justify-center text-center aspect-square",
                                selectedDistrict?.id === district.id
                                    ? "bg-primary text-black border-primary scale-105 shadow-xl shadow-primary/20 font-bold"
                                    : "bg-black/40 text-gray-300 border-white/10 hover:border-primary/50 hover:bg-black/60 hover:text-white"
                            )}
                        >
                            <MapPin className={cn("w-5 h-5 mb-2", selectedDistrict?.id === district.id ? "text-black" : "text-primary")} />
                            {district.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Info Panel */}
            {selectedDistrict && (
                <div className="bg-secondary rounded-xl p-8 border border-white/10 flex flex-col justify-center relative overflow-hidden">
                    {/* Background Decoration */}
                    <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
                        <MapPin size={150} />
                    </div>

                    <h2 className="text-3xl font-bold font-serif text-white mb-2">{selectedDistrict.name}</h2>
                    <div className="h-1 w-20 bg-primary mb-8" />

                    <div className="space-y-6 relative z-10">

                        <div className="flex items-start gap-3">
                            <div className="bg-primary/10 p-2 rounded text-primary">
                                <Users size={20} />
                            </div>
                            <div>
                                <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-1">Chef de Quartier</h4>
                                <p className="text-xl font-bold text-white">{selectedDistrict.chief_name || 'Non renseigné'}</p>
                                <p className="text-primary flex items-center gap-2 mt-1">
                                    <Phone size={14} />
                                    {selectedDistrict.chief_phone || 'Non renseigné'}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-4">
                            <div>
                                <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Calendar size={12} /> Création</h4>
                                <p className="text-white font-medium">{selectedDistrict.creation_date || '?'}</p>
                            </div>
                            <div>
                                <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Users size={12} /> Population</h4>
                                <p className="text-white font-medium">{selectedDistrict.population || '?'}</p>
                            </div>
                        </div>

                        <div className="bg-black/20 p-4 rounded-lg border border-white/5">
                            <h4 className="text-sm uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-2"><Info size={14} /> À propos</h4>
                            <p className="text-gray-300 text-sm leading-relaxed italic">
                                "{selectedDistrict.description || 'Aucune description disponible pour le moment.'}"
                            </p>
                        </div>

                    </div>
                </div>
            )}

        </div>
    );
}
